import { useEffect, useRef, useState } from "react";
import style from "./Tags.module.scss";
import { Chip } from "@ui";
import { IoMdClose } from "react-icons/io";
import { RiLoader5Fill } from "react-icons/ri";
import { MdDelete } from "react-icons/md";
import cn from "classnames";
import { IProxy } from "@interfaces/user/proxy";
import { ITag } from "@interfaces/user/tag";
import { useAddTagMutation, useDeleteTagMutation, useDisconnectTagMutation, useGetAllTagsQuery } from "@RTKQuery/user/tags/tags";
import { useAppSelector } from "store/hooks";

function* colorProxyTagGenerator() {
    const colors = ["purple", "orange", "blue", "green", "red"];
    let index = 0;
    while (true) {
        yield colors[index];
        index = (index + 1) % colors.length;
    }
}

const colorProxyTag = colorProxyTagGenerator();

function* colorListTagGenerator() {
    const colors = ["purple", "orange", "blue", "green", "red"];
    let index = 0;
    while (true) {
        yield colors[index];
        index = (index + 1) % colors.length;
    }
}

const colorListTag = colorListTagGenerator();

export function Tags({
    data
}: {
    data: IProxy
}) {
    return (
        <div className={style.list}>
            <AddTag proxy={data} />
            {
                data.tags.map(item => (
                    <Tag
                        key={item.id}
                        data={item}
                        proxy={data}
                    />
                ))
            }
        </div>
    )
}

function AddTag({
    proxy
}: {
    proxy: IProxy
}) {
    const { data } = useGetAllTagsQuery();

    const [addTag, { isLoading: addLoad }] = useAddTagMutation({
        fixedCacheKey: "add-tag"
    });

    const { activePaginationPage, activePaginationLimit, activeSearchParameter, activeTags } = useAppSelector(state => state.myProxiesUserPage);

    const [inputPaySum, setInputPaySum] = useState<string>("");
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const refContainer = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        function handleClick(e: any) {
            if (!refContainer.current?.contains(e.target)) {
                setIsOpen(false);
            }
        }

        document.documentElement.addEventListener("click", handleClick);

        return () => {
            document.documentElement.removeEventListener("click", handleClick);
        };
    }, []);

    return (
        <div ref={refContainer} className={style.addTag}>
            <div className="flex items-center gap-[5px]">
                <input
                    type="text"
                    className={style.input}
                    value={inputPaySum}
                    onChange={(e) => setInputPaySum(e.target.value)}
                    placeholder="Додати тег +"
                    onBlur={() => {
                        if (inputPaySum) {
                            addTag({
                                purchase_id: proxy.id,
                                body: {
                                    name: inputPaySum
                                },
                                params: {
                                    limit: activePaginationLimit,
                                    page: activePaginationPage,
                                    search: activeSearchParameter,
                                    tag_ids: activeTags
                                }
                            })
                                .unwrap()
                                .then(() => {
                                    setInputPaySum("")
                                })
                                .catch(() => { })
                        }
                    }}
                    onClick={() => setIsOpen(state => !state)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            if (inputPaySum) {
                                addTag({
                                    purchase_id: proxy.id,
                                    body: {
                                        name: inputPaySum
                                    },
                                    params: {
                                        limit: activePaginationLimit,
                                        page: activePaginationPage,
                                        search: activeSearchParameter,
                                        tag_ids: activeTags

                                    }
                                })
                                    .unwrap()
                                    .then(() => {
                                        setInputPaySum("")
                                    })
                                    .catch(() => { })
                            }
                        }
                    }}
                />
                {
                    addLoad ? <RiLoader5Fill className={"animate-spin"} /> : null
                }
            </div>
            <div className={cn(style.listTags, isOpen && "!flex")}>
                <div className="flex gap-[10px] flex-col overflow-y-auto overflow-x-hidden pr-[5px]" data-scrollable>
                    {
                        data?.map(item => (
                            <ListTagValue key={item.id} data={item} proxy={proxy} />
                        ))
                    }
                </div>
            </div>
        </div>
    )
}

function ListTagValue({
    proxy,
    data
}: {
    proxy: IProxy,
    data: ITag
}) {
    const [addTag] = useAddTagMutation({
        fixedCacheKey: "add-tag"
    });
    const [deleteTag, { isLoading: deleteLoad }] = useDeleteTagMutation();

    const { activePaginationPage, activePaginationLimit, activeSearchParameter, activeTags } = useAppSelector(state => state.myProxiesUserPage);

    const { current: color } = useRef(colorListTag.next().value as unknown as "purple" | "orange" | "red" | "blue" | "green")

    return (
        <div className="flex gap-[15px] items-center justify-between">
            <div
                className="cursor-pointer"
                onClick={() => {
                    addTag({
                        purchase_id: proxy.id,
                        body: {
                            id: data.id
                        },
                        params: {
                            limit: activePaginationLimit,
                            page: activePaginationPage,
                            search: activeSearchParameter,
                            tag_ids: activeTags

                        }
                    })
                }}
            >
                <Chip
                    text={"#" + data.name}
                    color={color}
                />
            </div>
            <div className="relative">
                <div
                    className={cn("absolute w-full  h-full left-0 top-0", !deleteLoad && "cursor-pointer")}
                    onClick={(e) => {
                        if (!deleteLoad) {
                            deleteTag({
                                tag_id: data.id
                            })
                        }
                    }}></div>
                {
                    deleteLoad
                        ? <RiLoader5Fill className={"animate-spin"} />
                        : <MdDelete />
                }
            </div>
        </div>
    )
}

function Tag({
    data,
    proxy,
}: {
    data: ITag,
    proxy: IProxy,
}) {
    const [disconnectTag, { isLoading }] = useDisconnectTagMutation();

    const { activePaginationPage, activePaginationLimit, activeSearchParameter, activeTags } = useAppSelector(state => state.myProxiesUserPage);

    const { current: color } = useRef(colorProxyTag.next().value as unknown as "purple" | "orange" | "red" | "blue" | "green")

    return (
        <Chip
            text={"#" + data.name}
            color={color}
            icon={
                isLoading
                    ? <RiLoader5Fill className={"animate-spin"} />
                    : <IoMdClose
                        className="cursor-pointer"
                        onClick={() => {
                            disconnectTag({
                                id: data.id,
                                purchase_id: proxy.id,
                                params: {
                                    limit: activePaginationLimit,
                                    page: activePaginationPage,
                                    search: activeSearchParameter,
                                    tag_ids: activeTags
                                }
                            })
                        }}
                    />
            }
        />
    )
}